import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Archive, Trash2, CheckCircle, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export function ArchivedPositionsDialog({ open, onOpenChange, positions, loading, onDelete }) {
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleClose = () => {
    setConfirmDeleteId(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-slate-600" />
            Archived Positions
          </DialogTitle>
          <DialogDescription>
            Positions that have been archived. You can permanently delete them here.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : positions.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No archived positions</h3>
              <p className="text-sm text-slate-500">Filled or cancelled positions can be archived from the main screen</p>
            </div>
          ) : (
            positions.map((position) => {
              const dates = position.dates || [];
              let dateDisplay;
              if (dates.length === 0) {
                const startDate = position.start_date || position.date;
                dateDisplay = startDate ? format(parseISO(startDate), 'EEEE, MMMM d, yyyy') : 'No date';
              } else if (dates.length === 1) {
                dateDisplay = format(parseISO(dates[0]), 'EEEE, MMMM d, yyyy');
              } else {
                dateDisplay = dates.map(d => format(parseISO(d), 'MMM d')).join(', ');
              }
              
              return (
                <Card key={position.position_id} className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge className={`status-${position.status}`}>
                            {position.status.charAt(0).toUpperCase() + position.status.slice(1)}
                          </Badge>
                          {dates.length > 1 && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                              {dates.length} days
                            </Badge>
                          )}
                          <span className="text-sm text-slate-500">{dateDisplay}</span>
                        </div>
                        <h4 className="font-semibold text-slate-900 text-sm">
                          {position.grade_level?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'General'}
                        </h4>
                        <p className="text-xs text-slate-600">{position.start_time} - {position.end_time}</p>
                        {position.accepted_by_name && (
                          <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Filled by {position.accepted_by_name}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {confirmDeleteId === position.position_id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-rose-600">Delete?</span>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => { onDelete(position.position_id); setConfirmDeleteId(null); }}
                              data-testid={`confirm-delete-position-${position.position_id}`}
                            >
                              Yes
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setConfirmDeleteId(null)}
                              data-testid={`cancel-delete-position-${position.position_id}`}
                            >
                              No
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-rose-600 border-rose-200 hover:bg-rose-50"
                            onClick={() => setConfirmDeleteId(position.position_id)}
                            data-testid={`delete-position-${position.position_id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="w-full sm:w-auto"
            data-testid="close-archive-btn"
          >
            Close Archive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
